'use client';

import { logger } from '@magicschool/logger';
import { DEFAULT_LOCALE, type Language } from '@magicschool/supabase/types';
import { isStudentHost, isTeacherHost } from '@magicschool/utils/nextjs/url';
import React, { useCallback, useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { setStudentLocale, setUserLocale } from '@/features/locales/actions';
import { getLocaleData, type LocaleData } from '@/features/locales/getLocaleData';
import { DEFAULT_RICH_TEXT_ELEMENTS } from '@/features/locales/richTextElements';
import { getLocaleFromDB } from '@/features/server/getLocale';
import { setCookie } from '@/util/cookies';
import { MILLISECONDS_IN_YEAR } from '@/util/date';
import { clientSupabase } from '@/util/supabase';

type LocaleContextType = {
  locale: Language;
  onLocaleChange: (locale: Language) => Promise<void>;
  isLoading: boolean;
};

const initialState: LocaleContextType = {
  locale: DEFAULT_LOCALE,
  onLocaleChange: async () => {
    // no-op
  },
  isLoading: false,
};

export const LocaleContext = React.createContext(initialState);

async function getAuthenticatedUserId(): Promise<string | undefined> {
  const { data, error } = await clientSupabase.auth.getClaims();
  if (error) {
    logger.warn('LocalesProvider.getAuthenticatedUserId: getClaims failed; treating user as unauthenticated', error);
    return undefined;
  }
  if (!data?.claims?.sub) {
    return undefined;
  }
  return data.claims.sub;
}

interface LocalsProps {
  locale: Language | undefined;
  children: React.ReactNode;
}

export const LocalesProvider = ({ children, locale: initialLocale = DEFAULT_LOCALE }: LocalsProps) => {
  const [locale, setLocale] = useState<Language>(initialLocale);
  const [messages, setMessages] = useState<LocaleData | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Load messages when locale changes
  useEffect(() => {
    const loadMessages = async () => {
      const data = await getLocaleData(locale);
      setMessages(data);
    };
    loadMessages();
  }, [locale]);

  // Sync locale from database when user logs in
  const syncLocaleFromDB = useCallback(async () => {
    if (hasInitialized) return; // Only sync once per session

    setIsLoading(true);
    try {
      const userLocale = await getLocaleFromDB();
      if (userLocale) {
        if (userLocale !== locale) {
          setLocale(userLocale);
        }
        // Seed the cookie so generateMetadata can skip the DB on subsequent navigations.
        setCookie('locale', userLocale, {
          expires: new Date(Date.now() + MILLISECONDS_IN_YEAR),
        });
      }
    } catch (error) {
      logger.error('Failed to sync locale from database:', error);
    } finally {
      setIsLoading(false);
      setHasInitialized(true);
    }
  }, [locale, hasInitialized]);

  // Handle auth state changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount
  useEffect(() => {
    const {
      data: { subscription },
    } = clientSupabase.auth.onAuthStateChange(async (event) => {
      if (event === 'SIGNED_IN') {
        const userId = await getAuthenticatedUserId();
        if (!userId) return;
        await syncLocaleFromDB();
      } else if (event === 'SIGNED_OUT') {
        setHasInitialized(false);
      }
    });

    // Check if already logged in on mount
    const checkInitialSession = async () => {
      const userId = await getAuthenticatedUserId();
      if (userId) {
        await syncLocaleFromDB();
      }
    };

    checkInitialSession();

    return () => subscription.unsubscribe();
  }, []);

  const onLocaleChange = useCallback(
    async (newLocale: Language) => {
      if (locale === newLocale) return;

      setIsLoading(true);
      try {
        const userId = await getAuthenticatedUserId();
        if (userId) {
          // User is authenticated - use server actions
          if (isStudentHost(window.location.host) && window.location.pathname.includes('s/rooms')) {
            await setStudentLocale(newLocale);
          } else if (isTeacherHost(window.location.host)) {
            await setUserLocale(newLocale);
          }
        }
      } catch (error) {
        logger.error('Error updating locale via server action:', error);
      }

      // Handle cookie for unauth users or fallback
      setCookie('locale', newLocale, {
        expires: new Date(Date.now() + MILLISECONDS_IN_YEAR),
      });

      // Update local state
      setLocale(newLocale);
      setIsLoading(false);
    },
    [locale],
  );

  return (
    <>
      {messages && (
        <IntlProvider
          locale={locale}
          defaultLocale={DEFAULT_LOCALE}
          messages={messages}
          defaultRichTextElements={DEFAULT_RICH_TEXT_ELEMENTS}
        >
          <LocaleContext.Provider
            value={{
              locale,
              onLocaleChange,
              isLoading,
            }}
          >
            {children}
          </LocaleContext.Provider>
        </IntlProvider>
      )}
    </>
  );
};
